/**
 * QUILL shared types (pure, no I/O) — the owner's writing voice + working-hours, stored in
 * `user.preferences.writingStyle` / `user.preferences.workingHours` and threaded into every prose
 * generator. Sibling of `prompt/userContext.ts` ("the ONE place who-the-user-is becomes prompt text"):
 * this is the ONE place "how the user sounds" and "when the user is free" become prompt/validation
 * inputs. See `docs/plans/writing-style.md` (QUILL-00/C0/05).
 */

/** The formality register a piece of prose is written in — picks the variant of the style block. */
export type ProseRegister = 'email' | 'chat' | 'digest' | 'spoken';

/** All registers, for iteration in tests/UI. */
export const PROSE_REGISTERS: readonly ProseRegister[] = ['email', 'chat', 'digest', 'spoken'] as const;

/** Cap on the free-text writing-style profile (mirrors VOICE_INSTRUCTIONS_MAX_CHARS — QUILL D5). */
export const WRITING_STYLE_MAX_CHARS = 4000;

/**
 * A linter rule the owner's own writing genuinely uses, so it is suppressed for this user (QUILL D17).
 * Keyed on the aiTells `ruleId`. `evidence` is the human-readable justification the bootstrap derived
 * ("em dash in 31% of your sent mail") shown in Settings so a whitelist entry is never a black box.
 */
export interface StyleWhitelistEntry {
  ruleId: string;
  evidence: string;
}

/** Minimum support before a bootstrap may whitelist a rule (QUILL §10 over-suppression mitigation). */
export const WHITELIST_MIN_OCCURRENCES = 5;
/** …and it must appear in at least this fraction of the sampled sent mail. */
export const WHITELIST_MIN_FREQUENCY = 0.1;

/**
 * A style rule the weekly edit-mining job distilled from the owner's draft edits (QUILL D7), awaiting
 * one-tap accept in Settings. Accepting appends `rule` to the profile text; never auto-applied.
 */
export interface ProposedStyleRule {
  id: string;
  /** The imperative rule ("Drop the opening pleasantry sentence"). */
  rule: string;
  /** What edit pattern it was mined from ("you deleted the greeting in 6 of 8 edited drafts"). */
  evidence: string;
  /** ISO timestamp the rule was proposed (stamped by the caller — core is time-free). */
  proposedAt?: string;
}

/**
 * The owner's writing voice. Only an `approved` profile is ever folded into a prompt (a freshly
 * bootstrapped one sits `proposed` until the owner edits/approves it in Settings — QUILL D5).
 */
export interface WritingStyleProfile {
  /** The approved (or proposed) profile text, folded verbatim into the style block. */
  profile: string;
  status: 'proposed' | 'approved';
  /** Rules the owner genuinely uses, suppressed in the linter (QUILL D17). */
  whitelist: StyleWhitelistEntry[];
  /** Mined-edit rule suggestions awaiting review (QUILL D7). */
  proposedRules: ProposedStyleRule[];
  /** ISO timestamp of the last bootstrap run (stamped by the caller). */
  bootstrappedAt?: string;
}

export function defaultWritingStyleProfile(): WritingStyleProfile {
  return { profile: '', status: 'proposed', whitelist: [], proposedRules: [] };
}

function cleanStr(v: unknown, max: number): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : '';
}

/** Coerce whatever is on disk into a clean {@link WritingStyleProfile}. Safe on undefined/garbage. */
export function coerceWritingStyleProfile(raw: unknown): WritingStyleProfile {
  if (!raw || typeof raw !== 'object') return defaultWritingStyleProfile();
  const r = raw as Record<string, unknown>;
  const whitelist: StyleWhitelistEntry[] = Array.isArray(r.whitelist)
    ? r.whitelist
        .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
        .map((e) => ({ ruleId: cleanStr(e.ruleId, 100), evidence: cleanStr(e.evidence, 300) }))
        .filter((e) => e.ruleId)
    : [];
  const proposedRules: ProposedStyleRule[] = Array.isArray(r.proposedRules)
    ? r.proposedRules
        .filter((e): e is Record<string, unknown> => !!e && typeof e === 'object')
        .map((e, i) => ({
          id: cleanStr(e.id, 100) || `r${i}`,
          rule: cleanStr(e.rule, 500),
          evidence: cleanStr(e.evidence, 500),
          ...(typeof e.proposedAt === 'string' ? { proposedAt: e.proposedAt } : {}),
        }))
        .filter((e) => e.rule)
    : [];
  return {
    profile: cleanStr(r.profile, WRITING_STYLE_MAX_CHARS),
    status: r.status === 'approved' ? 'approved' : 'proposed',
    whitelist,
    proposedRules,
    ...(typeof r.bootstrappedAt === 'string' ? { bootstrappedAt: r.bootstrappedAt } : {}),
  };
}

/** The profile text to use in a prompt: the approved profile, else '' (a proposed one is never used). */
export function activeProfileText(p: WritingStyleProfile | undefined): string {
  if (!p) return '';
  return p.status === 'approved' ? p.profile.trim() : '';
}

// --- Working hours (QUILL-C0 / D11) ------------------------------------------------------------

/**
 * The window inside which Lapo may propose meeting times, and which days count as working days.
 * `days` are JS weekday numbers (0=Sun … 6=Sat). `start`/`end` are local "HH:MM" 24h clock times.
 * `timeZone` is the IANA zone the clock is interpreted in; when absent the caller's agenda zone is used.
 */
export interface WorkingHours {
  days: number[];
  start: string;
  end: string;
  timeZone?: string;
}

export function defaultWorkingHours(): WorkingHours {
  return { days: [1, 2, 3, 4, 5], start: '09:00', end: '17:30' };
}

/** "HH:MM" → minutes past midnight, or null if malformed. */
export function parseClock(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Coerce stored working-hours into a valid {@link WorkingHours} (defaults on garbage / bad ranges). */
export function coerceWorkingHours(raw: unknown): WorkingHours {
  const def = defaultWorkingHours();
  if (!raw || typeof raw !== 'object') return def;
  const r = raw as Record<string, unknown>;
  const days = Array.isArray(r.days)
    ? Array.from(new Set(r.days.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6))).sort()
    : def.days;
  const start = typeof r.start === 'string' && parseClock(r.start) != null ? r.start : def.start;
  const end = typeof r.end === 'string' && parseClock(r.end) != null ? r.end : def.end;
  // A start >= end is nonsensical; fall back to the defaults rather than yield an empty window.
  const valid = (parseClock(start) ?? 0) < (parseClock(end) ?? 0);
  return {
    days: days.length ? days : def.days,
    start: valid ? start : def.start,
    end: valid ? end : def.end,
    ...(typeof r.timeZone === 'string' && r.timeZone ? { timeZone: r.timeZone } : {}),
  };
}
